/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';

export type ContentPart =
	| { type: 'text'; content: string }
	| { type: 'code'; language: string; content: string };

/**
 * Parses content string and splits it into text and code block parts
 */
export const parseContent = (content: string): ContentPart[] => {
	const parts: ContentPart[] = [];
	const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
	let lastIndex = 0;
	let match;

	while ((match = codeBlockRegex.exec(content)) !== null) {
		// Add text before the code block
		if (match.index > lastIndex) {
			parts.push({
				type: 'text',
				content: content.slice(lastIndex, match.index),
			});
		}

		// Add the code block
		parts.push({
			type: 'code',
			language: match[1] || 'text',
			content: match[2].trim(),
		});

		lastIndex = match.index + match[0].length;
	}

	// Add remaining text after the last code block
	if (lastIndex < content.length) {
		parts.push({
			type: 'text',
			content: content.slice(lastIndex),
		});
	}

	return parts.length > 0 ? parts : [{ type: 'text', content }];
};

/**
 * Renders text with basic markdown-style bold (**text**)
 */
export const renderTextWithBold = (text: string): (string | JSX.Element)[] => {
	const parts = text.split(/(\*\*[^*]+\*\*)/g);
	return parts.map((part, index) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<Text key={index} as="strong">
					{part.slice(2, -2)}
				</Text>
			);
		}
		return part;
	});
};
